import React, { useCallback, useState } from 'react';

import { Select } from '@aline/common';
import { Typography } from '@mui/material';
import { AlineButton } from 'src/components';
import { AddIcon } from 'src/components/icons/AddIcon';
import { PeopleSmallIcon } from 'src/components/icons/PeopleSmallIcon';
import { Apollo } from 'src/gql/generated';
import type { WorkflowStep } from 'src/workflow';
import {
  getWorkflowWasSentForSig,
  SignOutsideAline,
  StepsList,
  SwitchToSignOutsideAline,
} from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  groupListWrapper: {
    padding: theme.spacing(0, 1),
  },
  groupsSection: {
    border: `1px dashed ${theme.appColors.rule}`,
    borderRadius: 16,
    overflow: 'hidden',
    padding: theme.spacing(1),
  },
  groupsSectionHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
  },
  manageRecipientsRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
  },
  manageRecipientsTitle: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  peopleIcon: {
    color: theme.appColors.iconsType,
    height: 20,
    width: 20,
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  toggle: {
    margin: 0,
  },
}));

type ManageRecipientsWorkflow = Pick<
  Apollo.Workflow,
  'id' | 'isSignedOutsideAline' | '__typename' | 'state'
>;

interface ManageRecipientsSectionProps {
  activeStep: WorkflowStep | null;
  orderedSteps: WorkflowStep[];
  workflow: ManageRecipientsWorkflow;
  showProgress?: boolean;
}

export const ManageRecipientsSection = React.memo(function ManageRecipientsSection({
  activeStep,
  orderedSteps,
  showProgress = true,
  workflow,
}: ManageRecipientsSectionProps) {
  const { classes } = useStyles();
  const [newStepActive, setNewStepActive] = useState(false);
  const wasSentForSignature = getWorkflowWasSentForSig(workflow);
  const [setSignOutsideAlineMutation] = Apollo.useSetSignOutsideAlineMutation();

  const handleToggleSignOutsideAline = useCallback(async () => {
    await setSignOutsideAlineMutation({ variables: { workflowId: workflow.id } });
  }, [setSignOutsideAlineMutation, workflow.id]);

  return (
    <div className={classes.root}>
      <div className={classes.manageRecipientsRow}>
        <div className={classes.manageRecipientsTitle}>
          <PeopleSmallIcon className={classes.peopleIcon} />
          <Typography variant="subtitle2">Manage Recipients</Typography>
        </div>
        <SwitchToSignOutsideAline
          className={classes.toggle}
          toggle={handleToggleSignOutsideAline}
          toggled={workflow.isSignedOutsideAline}
          workflowId={workflow.id}
        />
      </div>
      {workflow.isSignedOutsideAline ? (
        <SignOutsideAline workflowId={workflow.id} />
      ) : (
        <div
          className={classes.groupsSection}
          data-cy={Select.workflow.detail.steps.signatureSection}
        >
          {!wasSentForSignature && (
            <div className={classes.groupsSectionHeader}>
              <AlineButton
                color="primary"
                data-cy={Select.workflow.detail.steps.action.addNewStepButton}
                startIcon={<AddIcon />}
                variant="outlined"
                onClick={() => setNewStepActive(true)}
              >
                Add Group
              </AlineButton>
            </div>
          )}
          <div className={classes.groupListWrapper}>
            <StepsList
              activeStep={activeStep}
              emptySectionContent={
                wasSentForSignature ? 'No signers were added.' : 'Add a group to add signers.'
              }
              newStepActive={newStepActive}
              orderedSteps={orderedSteps}
              setNewStepActive={setNewStepActive}
              showProgress={showProgress}
              workflowId={workflow.id}
              workflowState={workflow.state}
            />
          </div>
        </div>
      )}
    </div>
  );
});
